import SvgChevronRight from "@uui/ChevronRight";
import { useNavigate } from "react-router-dom";

import {
  Stack,
  styled,
  Table as MuiTable,
  TableContainer,
  Typography,
} from "@mui/material";

import TableBody from "@app/analytics/components/Table/TableBody";
import TableCell from "@app/analytics/components/Table/TableCell";
import TableHead from "@app/analytics/components/Table/TableHead";
import TableRow from "@app/analytics/components/Table/TableRow";
import useRollupContext from "@app/analytics/hooks/meetops/useRollupContext";
import { ALL_USERS } from "@app/analytics/providers/RollupContext";
import useDataTable, { HeadCell } from "@app/common/hooks/useDataTable";
import ROUTES from "@app/common/routes";

import ParticipationBreakdown from "./ParticipationBreakdown";
import { ParticipationTableData } from "./types";

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  ".MuiTableBody-root & .MuiTableCell-root": {
    ...theme.typography.bodySecondaryReg,
    height: "41px",
    padding: "0px",
    "&:first-of-type": {
      paddingLeft: "10px",
    },
    "&:not(:last-child)": {
      paddingRight: 0,
    },
  },
  "&:last-child": {
    ".MuiTableCell-body": {
      border: "none",
    },
  },
}));

const HEAD_CELLS: HeadCell<ParticipationTableData>[] = [
  {
    id: "name",
    label: "Team",
    sortable: true,
  },
  {
    id: "numMeetings",
    label: "Meetings",
    sortable: true,
    align: "center",
  },
  {
    id: "percentMeetingsWithLowParticipation",
    label: "Unbalanced Meetings",
    sortable: true,
  },
  {
    id: "actions",
    label: "",
    sortable: false,
  },
];

interface ParticipationTableProps {
  data: ParticipationTableData[];
  overallData: ParticipationTableData;
}

const ParticipationTable = ({ data, overallData }: ParticipationTableProps) => {
  const { handleSort, order, orderBy, tableRows } = useDataTable(
    data,
    HEAD_CELLS,
    {
      defaultOrderBy: "percentMeetingsWithLowParticipation",
    }
  );

  const navigate = useNavigate();
  const { setTeamFilter, setUsersFilter } = useRollupContext();
  const goToCmoDashboard = (team: ParticipationTableData) => () => {
    setTeamFilter({ id: team.id, label: team.name });
    setUsersFilter(ALL_USERS);
    navigate({
      pathname: ROUTES.LANDING_PAGE,
    });
  };

  return (
    <Stack sx={{ width: "100%", overflow: "hidden" }}>
      <TableContainer sx={{ maxHeight: "240px" }}>
        <MuiTable sx={{ border: "none" }}>
          <TableHead
            headCells={HEAD_CELLS}
            onSort={handleSort}
            order={order}
            orderBy={orderBy}
            sx={{ position: "sticky" }}
          />
          <TableBody sx={{ overflowY: "scroll" }}>
            <StyledTableRow
              key={overallData.id}
              sx={{ bgcolor: "shades.ghost.500" }}
            >
              <TableCell>
                <Typography
                  variant="bodySecondaryMed"
                  sx={{ color: "shades.primary.500" }}
                >
                  {overallData.name}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography
                  variant="bodySecondaryMed"
                  sx={{ color: "shades.primary.500" }}
                >
                  {overallData.numMeetings}
                </Typography>
              </TableCell>
              <TableCell>
                <ParticipationBreakdown
                  isOverall
                  lowParticipationPercent={
                    overallData.percentMeetingsWithLowParticipation
                  }
                />
              </TableCell>
              <TableCell />
            </StyledTableRow>
            {tableRows.map((team) => (
              <StyledTableRow
                key={team.id}
                onClick={goToCmoDashboard(team)}
                sx={{
                  "&:hover": {
                    backgroundColor: "states.hover",
                    cursor: "pointer",
                  },
                }}
              >
                <TableCell>
                  <Typography
                    variant="bodySecondaryReg"
                    sx={{ color: "shades.neutral.500" }}
                  >
                    {team.name}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography
                    variant="bodySecondaryReg"
                    sx={{ color: "shades.neutral.500" }}
                  >
                    {team.numMeetings}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <ParticipationBreakdown
                    lowParticipationPercent={
                      team.percentMeetingsWithLowParticipation
                    }
                  />
                </TableCell>
                <TableCell align="right">
                  <Stack alignItems="center">
                    <SvgChevronRight
                      sx={{
                        height: "20px",
                        width: "20px",
                        color: "shades.primary.500",
                      }}
                    />
                  </Stack>
                </TableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </MuiTable>
      </TableContainer>
    </Stack>
  );
};

export default ParticipationTable;
